import { isTruthy } from '@ardoq/common-helpers';
import {
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  PerspectivesGroupsReferenceTypes,
  PerspectivesGroupsWorkspace,
  ReferenceGroupingRule,
  toWorkspaceOption,
} from '@ardoq/perspectives';
import { TextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ArdoqId, ReferenceDirection } from '@ardoq/api-types';
import { RowHeader } from '../../atoms';
import { GroupNameHeaderInfoIcon } from '@ardoq/perspectives';

const toReferenceTypeOption = (
  referenceTypes: PerspectivesGroupsReferenceTypes
) => ({
  value: referenceTypes.id.toString(),
  label: referenceTypes.name,
});

const getReferenceDirectionOptions = (i18nLocale: I18nLocale) => [
  {
    value: ReferenceDirection.INCOMING,
    label: t(
      i18nLocale,
      'perspectivesSidebar:groupingRules.incoming',
      'Incoming'
    ),
  },
  {
    value: ReferenceDirection.OUTGOING,
    label: t(
      i18nLocale,
      'perspectivesSidebar:groupingRules.outgoing',
      'Outgoing'
    ),
  },
];

type ReferenceTypeGroupRuleProps = {
  groupingRule: ReferenceGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  referenceTypesPerWorkspace: Record<
    ArdoqId,
    PerspectivesGroupsReferenceTypes[]
  >;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  tabIndex?: number;
  i18nLocale?: I18nLocale;
};

const ReferenceTypeGroupRule = ({
  workspaces,
  groupingRule,
  referenceTypesPerWorkspace,
  onGroupingRuleUpdated,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale = {},
}: ReferenceTypeGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = [
    workspaceSelectOption,
    ...workspaces.map(workspace => toWorkspaceOption(workspace)),
  ]
    .filter(isTruthy)
    .map(option => ({
      ...option,
      ...(!referenceTypesPerWorkspace[option.value]?.length
        ? {
            isDisabled: true,
            popoverContent: t(
              i18nLocale,
              'perspectivesSidebar:groupingRules.noReferencesInWorkspace',
              'There are no references in this workspace for the opened datasets'
            ),
          }
        : {}),
    }));

  const referenceTypes =
    referenceTypesPerWorkspace[groupingRule.workspaceId!] ?? [];
  const referenceTypeOptions = [
    ruleTargetSelectOption,
    ...referenceTypes.map(toReferenceTypeOption),
  ].filter(isTruthy);

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.inWorkspace',
          'In workspace'
        )}
      </RowHeader>
      <Select
        dataTestId="reference-type-grouping-rule-workspace-select"
        value={groupingRule.workspaceId}
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultReferenceType =
            referenceTypesPerWorkspace[nextWorkspaceId]?.[0];
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultReferenceType?.id?.toString(),
          });
        }}
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(i18nLocale, 'perspectivesSidebar:groupingRules.type', 'Type')}
      </RowHeader>
      <Select
        dataTestId="reference-type-grouping-rule-reference-types-select"
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.referenceTypesAriaLabel',
          'Reference types'
        )}
        options={referenceTypeOptions}
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event => {
          updateGroupRule({
            targetId: event?.value,
          });
        }}
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.direction',
          'Direction'
        )}
      </RowHeader>
      <Select
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.referenceDirectionAriaLabel',
          'Reference direction'
        )}
        dataTestId="reference-type-grouping-rule-direction-select"
        value={groupingRule.direction}
        options={getReferenceDirectionOptions(i18nLocale)}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            direction: event?.value ?? ReferenceDirection.INCOMING,
          })
        }
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.groupName',
          'Group name'
        )}
        <GroupNameHeaderInfoIcon />
      </RowHeader>
      <TextInput
        value={groupingRule.sublabel}
        dataTestId="reference-type-grouping-rule-sublabel-input"
        placeholder={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.addLabelPlaceholder',
          'Add a label'
        )}
        tabIndex={tabIndex}
        onValueChange={value =>
          updateGroupRule({
            sublabel: value,
          })
        }
      />
    </>
  );
};

export { ReferenceTypeGroupRule };
